import { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Save } from 'lucide-react'
import type { AgentConfigListItem } from '../../../lib/api'
import { useUpdateAgentConfig } from '../../../hooks/useAIConfig'

interface BrandVoiceSections {
  identity: string
  differentiators: string
  tone: string
  avoid: string
  rest: string
}

function parseSections(prompt: string): BrandVoiceSections {
  const extract = (heading: string): string => {
    const regex = new RegExp(`##\\s*${heading}[\\s\\S]*?\\n([\\s\\S]*?)(?=\\n##|$)`, 'i')
    const match = prompt.match(regex)
    return match ? match[1].trim() : ''
  }

  return {
    identity: extract('Identidad'),
    differentiators: extract('Diferenciadores'),
    tone: extract('Tono|Estilo|Voz'),
    avoid: extract('Evitar|No usar'),
    rest: prompt,
  }
}

interface BrandVoicePanelProps {
  agents: AgentConfigListItem[]
  onAgentSelect: (agentId: string) => void
}

export function BrandVoicePanel({ agents, onAgentSelect }: BrandVoicePanelProps) {
  const planner = agents.find((a) => a.agent_id === 'content_planner')
  const updateAgent = useUpdateAgentConfig()
  const [showRaw, setShowRaw] = useState(false)
  const [sections, setSections] = useState<BrandVoiceSections>({
    identity: '',
    differentiators: '',
    tone: '',
    avoid: '',
    rest: '',
  })
  const [rawPrompt, setRawPrompt] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (!planner) return
    const effectivePrompt =
      planner.override?.system_prompt ?? planner.default?.system_prompt ?? ''
    const parsed = parseSections(effectivePrompt)
    setSections(parsed)
    setRawPrompt(effectivePrompt)
    setIsDirty(false)
  }, [planner])

  const handleRawChange = (val: string) => {
    setRawPrompt(val)
    setSections(parseSections(val))
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!planner) return
    await updateAgent.mutateAsync({
      agentKey: planner.agent_id,
      data: { system_prompt: rawPrompt },
    })
    setIsDirty(false)
  }

  if (!planner) return null

  const hasContent = sections.identity || sections.differentiators || sections.tone

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Voz de Marca</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onAgentSelect('content_planner')}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Editar agente completo
          </button>
          {isDirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={updateAgent.isPending}
              className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-3 h-3" />
              {updateAgent.isPending ? 'Guardando...' : 'Guardar cambios'}
            </button>
          )}
        </div>
      </div>

      {hasContent ? (
        <div className="space-y-3">
          {sections.identity && (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider block mb-1">
                Identidad
              </label>
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">
                {sections.identity}
              </p>
            </div>
          )}
          {sections.differentiators && (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider block mb-1">
                Diferenciadores
              </label>
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">
                {sections.differentiators}
              </p>
            </div>
          )}
          {sections.tone && (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider block mb-1">
                Tono y Estilo
              </label>
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">
                {sections.tone}
              </p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400">
          No se detectaron secciones estructuradas en el prompt del Planificador.{' '}
          <button
            type="button"
            onClick={() => setShowRaw(true)}
            className="text-emerald-600 hover:underline"
          >
            Editar prompt completo
          </button>
        </p>
      )}

      <div className="border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
        >
          {showRaw ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {showRaw ? 'Ocultar' : 'Ver'} prompt completo (raw)
        </button>
        {showRaw && (
          <textarea
            className="mt-2 input font-mono text-xs w-full"
            rows={10}
            value={rawPrompt}
            onChange={(e) => handleRawChange(e.target.value)}
            style={{ resize: 'vertical', minHeight: '160px' }}
          />
        )}
      </div>
    </div>
  )
}
